import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { LineReveal, FadeUp } from "../components/Reveal";

const STAGES = ["Chaos", "Clarity", "Possibility"];

export const BigIdea = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [reduce ? 0 : 40, reduce ? 0 : -40]);
  const lineScale = useTransform(scrollYProgress, [0.2, 0.6], [0, 1]);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden bg-plum"
      aria-label="The big idea"
      data-testid="big-idea-section"
    >
      <div className="grain relative mx-auto grid max-w-[1280px] grid-cols-1 items-center gap-14 px-5 py-24 sm:px-8 lg:grid-cols-12 lg:py-32">
        <FadeUp className="lg:col-span-5">
          <div className="relative">
            <div aria-hidden="true" className="absolute -inset-4 rounded-[2.2rem] bg-ivory/[0.06]" />
            <div className="relative overflow-hidden rounded-3xl border border-ivory/15 shadow-[0_30px_70px_rgba(0,0,0,0.3)]">
              <motion.img
                src="/assets/bigidea.png"
                alt="A woman's desk blending work and family life: planner, laptop, a child's toy and blush roses"
                className="aspect-[4/5] w-full scale-[1.08] object-cover"
                width={760}
                height={950}
                loading="lazy"
                style={{ y: imgY }}
              />
            </div>
          </div>
        </FadeUp>
        <div className="lg:col-span-7 lg:pl-6">
          <LineReveal
            as="h2"
            lines={["What if getting your life together", "didn't mean putting yourself last?"]}
            className="font-serif text-4xl font-medium leading-[1.1] tracking-tight text-ivory sm:text-5xl"
          />
          <FadeUp delay={0.2} className="mt-7 max-w-xl space-y-4 text-base leading-relaxed text-ivory/75 sm:text-lg">
            <p>BloomParentify was built around a simple idea:</p>
            <p className="font-serif text-2xl italic leading-snug text-blush">
              You shouldn't have to choose between being present for your family,
              doing well at work and building something for yourself.
            </p>
          </FadeUp>
          <div className="mt-10 max-w-xl" aria-hidden="true">
            <div className="flex items-center justify-between gap-2">
              {STAGES.map((s, i) => (
                <FadeUp key={s} delay={0.3 + i * 0.35}>
                  <span
                    className={`rounded-xl border px-4 py-2 font-serif text-base italic sm:text-lg ${
                      i === 2
                        ? "border-gold/50 bg-gold/10 text-gold"
                        : "border-ivory/20 bg-ivory/[0.06] text-ivory/70"
                    }`}
                  >
                    {s}
                  </span>
                </FadeUp>
              ))}
            </div>
            <motion.div
              style={{ scaleX: reduce ? 1 : lineScale }}
              className="mt-3 h-px origin-left bg-gradient-to-r from-ivory/20 via-blush/50 to-gold"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
